import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Group,
  Button,
  Stepper,
  Box,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  ArrowLeft,
  ArrowRight,
} from 'react-feather';
import { cohortsApi } from '../../../services/api/cohorts';
import { programsApi } from '../../../services/api/programs';
import { partnersApi } from '../../../services/api/partners';
import { usersApi, type InternalUser } from '../../../services/api/users';
import { timeSlotsApi } from '../../../services/api/timeSlots';
import { useJobTitles } from '../../../hooks/useJobTitles';
import { useAuthStore } from '../../../stores/auth';
import {
  readWizardDraftJson,
  removeWizardDraft,
  writeWizardDraftJson,
  type TimeSlotRecovery,
} from '../../../utils/cohortWizardDraft';
import type {
  Partner,
  Program,
  CreateCohortRequest,
  PartnerContactWithCohortCount,
  ModalityType,
  CreateModalityInput,
} from '../../../types';
import {
  ensureDate,
  toISODateString,
  isDateInConstraint,
  type DateConstraint,
} from '../../../utils/dateHelpers';
import {
  DEFAULT_CHECKIN_SETTINGS,
  applyCheckinEdit,
  initialCheckinAuto,
  oneOnOneTargetWeek,
  programWeeks,
  refreshAutoWeeks,
} from '../../../utils/checkinWeeks';
import type { CheckinSettingsForm } from '../components/CheckinSettingsFields';
import { deriveCohortDateRange, wizardProgramWeeks, wizardCheckinsError } from './cohortDateRange';
import type {
  FormValues,
  FormDateConstraint,
  FormWorkshopInput,
  FormTimeSlot,
  ModalitySessionDates,
} from './types';
import {
  DEFAULT_GROUP_COACHING_CONFIG,
  DEFAULT_ONE_ON_ONE_CONFIG,
  DEFAULT_WORKSHOP_CONFIG,
  DEFAULT_WORKSHOP_INPUT,
  DEFAULT_COHORT_DATES,
} from './types';
import { PartnerProgramStep } from './PartnerProgramStep';
import { CohortDetailsStep } from './CohortDetailsStep';
import { ModalitiesStep } from './ModalitiesStep';
import { SessionConfigStep } from './SessionConfigStep';
import { DatesStep } from './DatesStep';
import { ReviewStep } from './ReviewStep';
import { TimeSlotSaveFailureAlert } from './TimeSlotSaveFailureAlert';
import {
  checkCohortStillExists,
  cohortDeletedNotification,
  createTimeSlotsInOrder,
  formatTimeSlot,
  isCohortNotFoundError,
  keepListConfirmText,
  timeSlotFailureMessage,
  timeSlotFailureNotification,
  timeSlotsSavedNotification,
  type WizardNotification,
} from './timeSlotCreation';
import { isBlankDraft } from './wizardDraft';
import { partnerProgramErrors } from './wizardValidation';
import classes from './CreateCohortPage.module.css';

// MED-1268: the draft is stored per user (utils/cohortWizardDraft), so one
// user's list of unsaved slots never shows to the next user of the browser.
// Each try gets its own notification id, so a failed retry shows a new
// notification instead of leaving the old one in place.
const TIME_SLOT_FAILURE_NOTIFICATION_PREFIX = 'create-cohort-time-slots-failed';

// MED-1268: TimeSlotRecovery is set when the cohort exists but some time
// slots were not saved. Kept in the draft, so a reload resumes the retry and
// never creates the cohort twice.
interface WizardDraft {
  step: number;
  values: FormValues;
  timeSlotRecovery?: TimeSlotRecovery | null;
}

// MED-1350: check-ins hang off group or 1:1 coaching. With neither, the
// toggles are hidden and the cohort is created with both check-ins off.
function activeCheckins(values: FormValues): FormValues['checkins'] {
  const { selectedModalities } = values;
  return selectedModalities.includes('group_coaching') || selectedModalities.includes('one_on_one')
    ? values.checkins
    : DEFAULT_CHECKIN_SETTINGS;
}

// Load the saved draft of this user from localStorage
function loadDraft(userId: string | null | undefined): WizardDraft | null {
  try {
    const saved = readWizardDraftJson(localStorage, userId);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Restore Date objects for sessionDates (by modality) and dateConstraints
      if (parsed.values.sessionDates) {
        const restored: ModalitySessionDates = {
          group_coaching: [],
          one_on_one: [],
          workshop: [],
        };
        for (const key of ['group_coaching', 'one_on_one', 'workshop'] as ModalityType[]) {
          if (parsed.values.sessionDates[key]) {
            restored[key] = parsed.values.sessionDates[key].map((d: string | null) =>
              d ? new Date(d) : null
            );
          }
        }
        parsed.values.sessionDates = restored;
      }
      if (parsed.values.dateConstraints) {
        parsed.values.dateConstraints = parsed.values.dateConstraints.map((c: FormDateConstraint) => ({
          ...c,
          start_date: c.start_date ? new Date(c.start_date) : null,
          end_date: c.end_date ? new Date(c.end_date) : null,
        }));
      }
      // Ensure timeSlots array exists (for drafts created before this field was added)
      if (!parsed.values.timeSlots) {
        parsed.values.timeSlots = [];
      }
      // MED-229: Ensure sessionDurationMinutes fields exist (for drafts created before this feature)
      if (parsed.values.groupCoachingConfig && parsed.values.groupCoachingConfig.sessionDurationMinutes === undefined) {
        parsed.values.groupCoachingConfig.sessionDurationMinutes = DEFAULT_GROUP_COACHING_CONFIG.sessionDurationMinutes;
      }
      // MED-948 (E1.10a): default the new Live Session Delivery gate for drafts
      // created before this field existed (keeps the Switch controlled).
      if (parsed.values.groupCoachingConfig && parsed.values.groupCoachingConfig.liveSessionDeliveryEnabled === undefined) {
        parsed.values.groupCoachingConfig.liveSessionDeliveryEnabled = DEFAULT_GROUP_COACHING_CONFIG.liveSessionDeliveryEnabled;
      }
      if (parsed.values.oneOnOneConfig && parsed.values.oneOnOneConfig.sessionDurationMinutes === undefined) {
        parsed.values.oneOnOneConfig.sessionDurationMinutes = DEFAULT_ONE_ON_ONE_CONFIG.sessionDurationMinutes;
      }
      if (parsed.values.oneOnOneConfig && !parsed.values.oneOnOneConfig.sessionConfigs) {
        parsed.values.oneOnOneConfig.sessionConfigs = [];
      }
      // MED-1350: drafts saved before check-ins start with both check-ins off.
      if (!parsed.values.checkins) {
        parsed.values.checkins = DEFAULT_CHECKIN_SETTINGS;
      }
      // Ensure workshop inputs have sessionDurationMinutes
      if (parsed.values.workshopConfig?.workshops) {
        parsed.values.workshopConfig.workshops = parsed.values.workshopConfig.workshops.map((w: FormWorkshopInput) => ({
          ...w,
          sessionDurationMinutes: w.sessionDurationMinutes ?? DEFAULT_WORKSHOP_INPUT.sessionDurationMinutes,
        }));
      }
      // MED-371: Restore cohortDates — migrate old { date, time } format to Date | null
      if (parsed.values.cohortDates) {
        const cd = parsed.values.cohortDates;
        // Migrate coachImmersion
        if (cd.coachImmersion && typeof cd.coachImmersion === 'object' && 'date' in cd.coachImmersion) {
          const { date, time } = cd.coachImmersion as { date: string | null; time: string };
          cd.coachImmersion = ensureDate(date);
          if (cd.coachImmersion && time) {
            const [h, m] = time.split(':').map(Number);
            if (!isNaN(h)) cd.coachImmersion.setHours(h, m || 0, 0, 0);
          }
        } else if (typeof cd.coachImmersion === 'string') {
          cd.coachImmersion = ensureDate(cd.coachImmersion);
        }
        // Migrate participantOrientation
        if (cd.participantOrientation && typeof cd.participantOrientation === 'object' && 'date' in cd.participantOrientation) {
          const { date, time } = cd.participantOrientation as { date: string | null; time: string };
          cd.participantOrientation = ensureDate(date);
          if (cd.participantOrientation && time) {
            const [h, m] = time.split(':').map(Number);
            if (!isNaN(h)) cd.participantOrientation.setHours(h, m || 0, 0, 0);
          }
        } else if (typeof cd.participantOrientation === 'string') {
          cd.participantOrientation = ensureDate(cd.participantOrientation);
        }
        // Migrate coachHuddles array
        if (Array.isArray(cd.coachHuddles)) {
          cd.coachHuddles = cd.coachHuddles.map((h: unknown) => {
            if (h && typeof h === 'object' && 'date' in (h as Record<string, unknown>)) {
              const { date, time } = h as { date: string | null; time: string };
              if (!date) return null;
              const d = ensureDate(date)!;
              if (time) {
                const [hr, mn] = time.split(':').map(Number);
                if (!isNaN(hr)) d.setHours(hr, mn || 0, 0, 0);
              }
              return d;
            }
            if (typeof h === 'string') return ensureDate(h);
            return h;
          });
        }
        // Restore cohortWrapUp
        if (typeof cd.cohortWrapUp === 'string') {
          cd.cohortWrapUp = ensureDate(cd.cohortWrapUp);
        }
        parsed.values.cohortDates = cd;
      }
      return parsed;
    }
  } catch (e) {
    console.error('Failed to load draft:', e);
  }
  return null;
}

const INITIAL_VALUES: FormValues = {
  // Step 1
  partnerId: '',
  primaryContactIds: [],
  programId: '',
  newProgramName: '',
  // Step 2
  name: '',
  shortName: '',
  code: '',
  defaultMeetingPlatform: '',
  // Step 3
  selectedModalities: [],
  // Step 4
  groupCoachingConfig: DEFAULT_GROUP_COACHING_CONFIG,
  oneOnOneConfig: DEFAULT_ONE_ON_ONE_CONFIG,
  workshopConfig: DEFAULT_WORKSHOP_CONFIG,
  preferredDays: [],
  preferredTimes: [],
  // MED-1350: new cohorts start with both check-ins off.
  checkins: DEFAULT_CHECKIN_SETTINGS,
  // Step 5
  dateConstraints: [],
  cohortDates: DEFAULT_COHORT_DATES,
  sessionDates: {
    group_coaching: [],
    one_on_one: [],
    workshop: [],
  },
  timeSlots: [],
  // Step 6 (keyed by job_title_id)
  teamAssignments: {} as Record<string, string>,
};

// Props for modal mode
interface CreateCohortModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export function CreateCohortModal({ opened, onClose, onCreated }: CreateCohortModalProps) {
  const navigate = useNavigate();
  const { jobTitleGroups } = useJobTitles();
  // MED-1268: the draft belongs to the logged-in user.
  const userId = useAuthStore(state => state.user?.id ?? null);
  // MED-1268: start at the draft's step. The auto-save runs on mount, and a
  // start at 0 wrote step 0 over the saved step before it was restored.
  const [active, setActive] = useState(() => loadDraft(userId)?.step ?? 0);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [partnerContacts, setPartnerContacts] = useState<PartnerContactWithCohortCount[]>([]);
  const [internalUsers, setInternalUsers] = useState<InternalUser[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(true);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [creating, setCreating] = useState(false);
  const [checkingCode, setCheckingCode] = useState(false);
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null);
  const [checkingName, setCheckingName] = useState(false);
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null);
  const [checkingShortName, setCheckingShortName] = useState(false);
  const [shortNameAvailable, setShortNameAvailable] = useState<boolean | null>(null);
  const [checkingCohort, setCheckingCohort] = useState(false);

  // Save draft to localStorage, under this user's key
  const saveDraft = useCallback((step: number, values: FormValues, timeSlotRecovery: TimeSlotRecovery | null) => {
    const draftToSave: WizardDraft = { step, values, timeSlotRecovery };
    writeWizardDraftJson(localStorage, userId, JSON.stringify(draftToSave));
  }, [userId]);

  // Clear this user's draft from localStorage
  const clearDraft = useCallback(() => {
    removeWizardDraft(localStorage, userId);
  }, [userId]);

  // Initialize from saved draft
  const draft = loadDraft(userId);

  const form = useForm<FormValues>({
    initialValues: draft?.values || INITIAL_VALUES,
  });

  // MED-1268: set when the cohort was created but some time slots were not
  // saved. The ref lets the auto-save read it without re-saving a draft that
  // was just cleared.
  const [timeSlotRecovery, setTimeSlotRecovery] = useState<TimeSlotRecovery | null>(
    () => draft?.timeSlotRecovery ?? null,
  );
  const timeSlotRecoveryRef = useRef<TimeSlotRecovery | null>(timeSlotRecovery);
  const updateTimeSlotRecovery = (next: TimeSlotRecovery | null) => {
    timeSlotRecoveryRef.current = next;
    setTimeSlotRecovery(next);
  };
  const failureNotificationIdRef = useRef<string | null>(null);
  const hideFailureNotification = () => {
    if (failureNotificationIdRef.current) {
      notifications.hide(failureNotificationIdRef.current);
      failureNotificationIdRef.current = null;
    }
  };

  // MED-1268: Discard empties the wizard. form.reset() alone goes back to the
  // initial values, which came from the draft, and the auto-save wrote them
  // back. The auto-save removes the stored draft once the wizard is blank.
  const discardWizard = () => {
    updateTimeSlotRecovery(null);
    hideFailureNotification();
    form.setInitialValues(INITIAL_VALUES);
    form.setValues(INITIAL_VALUES);
    resetCheckinAuto(INITIAL_VALUES);
    clearDraft();
    setActive(0);
  };

  // Restore active step from draft (only on mount)
  useEffect(() => {
    if (draft?.step !== undefined) {
      setActive(draft.step);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save draft when form values or step changes. A blank wizard keeps no
  // draft, so a discarded wizard opens empty.
  useEffect(() => {
    const next: WizardDraft = { step: active, values: form.values, timeSlotRecovery: timeSlotRecoveryRef.current };
    if (isBlankDraft(next, INITIAL_VALUES)) {
      clearDraft();
    } else {
      saveDraft(active, form.values, timeSlotRecoveryRef.current);
    }
  }, [active, form.values, saveDraft, clearDraft]);

  // MED-1350: a target week stays "auto" until the CM types one. An auto week
  // follows the default for the program length each time the session dates
  // change it. A typed week is never changed. Not saved in the draft: a
  // resumed draft treats a stored week as auto only when it equals the
  // default it would get now (initialCheckinAuto).
  const programWeeksFromDates = wizardProgramWeeks(form.values);
  const checkinAuto = useRef(initialCheckinAuto(form.values.checkins, programWeeksFromDates));
  const resetCheckinAuto = (values: FormValues) => {
    checkinAuto.current = initialCheckinAuto(values.checkins, wizardProgramWeeks(values));
  };
  const handleCheckinsChange = (next: CheckinSettingsForm) => {
    const result = applyCheckinEdit(form.values.checkins, next, checkinAuto.current, programWeeksFromDates);
    checkinAuto.current = result.auto;
    form.setFieldValue('checkins', result.value);
    form.clearFieldError('checkins');
  };
  useEffect(() => {
    const { checkins } = form.values;
    const refreshed = refreshAutoWeeks(checkins, checkinAuto.current, programWeeksFromDates);
    if (refreshed !== checkins) {
      form.setFieldValue('checkins', refreshed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programWeeksFromDates]);

  // Update session dates array when session count changes (per modality)
  useEffect(() => {
    const { selectedModalities, groupCoachingConfig, oneOnOneConfig, workshopConfig, sessionDates } = form.values;
    const newSessionDates = { ...sessionDates };
    let changed = false;

    if (selectedModalities.includes('group_coaching')) {
      const targetCount = groupCoachingConfig.sessionCount;
      if (newSessionDates.group_coaching.length !== targetCount) {
        const current = [...newSessionDates.group_coaching];
        while (current.length < targetCount) current.push(null);
        current.length = targetCount;
        newSessionDates.group_coaching = current;
        changed = true;
      }
    }

    if (selectedModalities.includes('one_on_one')) {
      const targetCount = oneOnOneConfig.sessionCount;
      if (newSessionDates.one_on_one.length !== targetCount) {
        const current = [...newSessionDates.one_on_one];
        while (current.length < targetCount) current.push(null);
        current.length = targetCount;
        newSessionDates.one_on_one = current;
        changed = true;
      }
    }

    if (selectedModalities.includes('workshop')) {
      const targetCount = workshopConfig.workshops.length;
      if (newSessionDates.workshop.length !== targetCount) {
        const current = [...newSessionDates.workshop];
        while (current.length < targetCount) current.push(null);
        current.length = targetCount;
        newSessionDates.workshop = current;
        changed = true;
      }
    }

    if (changed) {
      form.setFieldValue('sessionDates', newSessionDates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.selectedModalities, form.values.groupCoachingConfig.sessionCount, form.values.oneOnOneConfig.sessionCount, form.values.workshopConfig.workshops.length]);

  // Fetch partners and internal users on mount
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setLoadingPartners(true);
        const response = await partnersApi.list({ limit: 100, sortBy: 'name', sortOrder: 'ASC' });
        setPartners(response.data);
      } catch {
        notifications.show({
          title: 'Error',
          message: 'Failed to fetch partners',
          color: 'red',
        });
      } finally {
        setLoadingPartners(false);
      }
    };

    const fetchInternalUsers = async () => {
      try {
        const users = await usersApi.listInternal();
        setInternalUsers(users);
      } catch (err) {
        console.error('Failed to fetch internal users:', err);
      }
    };

    fetchPartners();
    fetchInternalUsers();
  }, []);

  // Fetch programs and contacts when partner changes
  useEffect(() => {
    const fetchPrograms = async () => {
      if (!form.values.partnerId) {
        setPrograms([]);
        return;
      }

      try {
        setLoadingPrograms(true);
        const response = await programsApi.list({ partnerId: form.values.partnerId, limit: 100 });
        setPrograms(response.data);
        // MED-1268: keep the program of a resumed draft when it belongs to this
        // partner. Clear it only when it does not.
        const programId = form.values.programId;
        if (programId !== '__create_new__' && !response.data.some(program => program.id === programId)) {
          form.setFieldValue('programId', '');
        }
      } catch {
        notifications.show({
          title: 'Error',
          message: 'Failed to fetch programs',
          color: 'red',
        });
      } finally {
        setLoadingPrograms(false);
      }
    };

    const fetchContacts = async () => {
      if (!form.values.partnerId) {
        setPartnerContacts([]);
        return;
      }

      try {
        setLoadingContacts(true);
        const contacts = await partnersApi.listContacts(form.values.partnerId);
        setPartnerContacts(contacts);
        // MED-1268: keep the contacts of a resumed draft that belong to this
        // partner.
        const contactIds = new Set(contacts.map(contact => contact.id));
        const keptIds = form.values.primaryContactIds.filter(id => contactIds.has(id));
        if (keptIds.length !== form.values.primaryContactIds.length) {
          form.setFieldValue('primaryContactIds', keptIds);
        }
      } catch (err) {
        console.error('Failed to fetch partner contacts:', err);
        setPartnerContacts([]);
      } finally {
        setLoadingContacts(false);
      }
    };

    fetchPrograms();
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.partnerId]);

  // Check code availability
  const checkCodeAvailability = useCallback(async (code: string) => {
    if (!code || code.length < 2) {
      setCodeAvailable(null);
      return;
    }
    try {
      setCheckingCode(true);
      const result = await cohortsApi.checkCode(code);
      setCodeAvailable(result.available);
    } catch {
      setCodeAvailable(null);
    } finally {
      setCheckingCode(false);
    }
  }, []);

  // Check name availability (case-insensitive)
  const checkNameAvailability = useCallback(async (name: string) => {
    if (!name || name.trim().length < 2) {
      setNameAvailable(null);
      return;
    }
    try {
      setCheckingName(true);
      const result = await cohortsApi.checkAvailability({ name: name.trim() });
      setNameAvailable(result.nameAvailable ?? null);
    } catch {
      setNameAvailable(null);
    } finally {
      setCheckingName(false);
    }
  }, []);

  // Check short name availability (case-insensitive)
  const checkShortNameAvailability = useCallback(async (shortName: string) => {
    if (!shortName || shortName.trim().length < 2) {
      setShortNameAvailable(null);
      return;
    }
    try {
      setCheckingShortName(true);
      const result = await cohortsApi.checkAvailability({ shortName: shortName.trim() });
      setShortNameAvailable(result.shortNameAvailable ?? null);
    } catch {
      setShortNameAvailable(null);
    } finally {
      setCheckingShortName(false);
    }
  }, []);

  // Accepts a target step so the same rules can be re-run on submit (AC6:
  // a resumed draft can restore a later step, bypassing per-step gating).
  const validateStep = (step: number = active): boolean => {
    const errors: Record<string, string | null> = {};

    switch (step) {
      case 0: // Partner & Program
        // MED-1268: Program carries a required mark, so it is required.
        Object.assign(errors, partnerProgramErrors(form.values));
        break;

      case 1: // Cohort Details
        // Presence checks first, then uniqueness — the required asterisk on
        // Name, Short Name, and Code must be genuinely enforced (AC1–AC3).
        if (!form.values.name.trim()) {
          errors.name = 'Cohort name is required';
        } else if (nameAvailable === false) {
          errors.name = 'This cohort name is already in use';
        }
        if (!form.values.shortName.trim()) {
          errors.shortName = 'Cohort short name is required';
        } else if (shortNameAvailable === false) {
          errors.shortName = 'This short name is already in use';
        }
        if (!form.values.code.trim()) {
          errors.code = 'Cohort code is required';
        } else if (codeAvailable === false) {
          errors.code = 'This code is already in use';
        }
        break;

      case 2: // Modalities
        if (form.values.selectedModalities.length === 0) {
          errors.selectedModalities = 'At least one modality is required';
        }
        // MED-848 AC3: sub-type required when group_coaching is selected.
        if (
          form.values.selectedModalities.includes('group_coaching') &&
          !form.values.groupCoachingConfig.coachingSubType
        ) {
          errors['groupCoachingConfig.coachingSubType'] =
            'Coaching sub-type is required for Group Coaching';
        }
        break;

      case 3: // Session Configuration
        // Optional validation for workshops
        if (form.values.selectedModalities.includes('workshop')) {
          const { workshops } = form.values.workshopConfig;
          if (workshops.some(w => !w.title.trim())) {
            errors.workshops = 'All workshops must have a title';
          }
        }
        // MED-1350: the check-in target weeks are set and checked on the
        // Dates step, so this step does not check them.
        break;

      case 4: // Dates
        // MED-372: cohort_wrap_up_date is now optional
        if (form.values.cohortDates.cohortWrapUp) {
          // MED-234: Validate that no session dates exceed the wrap date (only when set)
          const wrapDate = form.values.cohortDates.cohortWrapUp;
          const allModalities: ModalityType[] = ['group_coaching', 'one_on_one', 'workshop'];
          let hasSessionPastWrap = false;

          for (const modality of allModalities) {
            if (!form.values.selectedModalities.includes(modality)) continue;
            for (const date of form.values.sessionDates[modality]) {
              if (date && date > wrapDate) {
                hasSessionPastWrap = true;
                break;
              }
            }
            if (hasSessionPastWrap) break;
          }

          if (hasSessionPastWrap) {
            errors.cohortWrapUp = 'Session dates extend past the cohort wrap date. Please update the wrap date.';
          }
        }
        // MED-1350: with the dates known, each target week must fit the program.
        {
          const checkinError = wizardCheckinsError(activeCheckins(form.values), form.values);
          if (checkinError) errors.checkins = checkinError;
        }
        break;
    }

    form.setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // MED-1350: each step opens at its top. The step area scrolls on its own, so
  // it kept the scroll position of the step before.
  const stepContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    stepContentRef.current?.scrollTo({ top: 0 });
  }, [active]);

  const nextStep = () => {
    if (!validateStep()) return;
    setActive(current => (current < 5 ? current + 1 : current));
  };

  const prevStep = () => setActive(current => (current > 0 ? current - 1 : current));

  // MED-1268: the cohort and all its time slots are saved. Only now is the
  // draft cleared and the success message shown. A retry that saved the
  // slots says so; it does not say the cohort was created.
  const finishCreation = (cohortId: string, success: WizardNotification) => {
    updateTimeSlotRecovery(null);
    clearDraft();
    hideFailureNotification();
    notifications.show({ ...success, color: 'green' });

    onClose();
    onCreated?.();
    navigate(`/cohorts/${cohortId}`);
  };

  // MED-1268: saves the slots in order and stops at the first failure, so each
  // slot gets the slot number of its place in the list. When a slot fails, the
  // wizard stays open with the list of unsaved slots, and the draft is kept.
  // Returns true when every slot saved.
  const saveTimeSlots = async (
    cohortId: string,
    cohortName: string,
    slotsToSave: FormTimeSlot[],
  ): Promise<boolean> => {
    const { failed } = await createTimeSlotsInOrder(
      slotsToSave,
      request => timeSlotsApi.create(cohortId, request),
    );
    if (failed.length === 0) return true;

    // The failed request is also in the API request log (warn level, with the
    // status code) when it reached the server. Later slots were not sent.
    const firstFailure = failed[0];
    // The cohort was deleted after it was created. No retry can save its slots.
    if (isCohortNotFoundError(firstFailure.error)) {
      handleCohortDeleted(cohortName);
      return false;
    }
    console.error(
      `Failed to create time slot ${formatTimeSlot(firstFailure.slot)} for cohort ${cohortId}. ` +
        `${failed.length - 1} later time slot(s) were not sent:`,
      firstFailure.error,
    );

    const attempts = (timeSlotRecoveryRef.current?.attempts ?? 0) + 1;
    const recovery: TimeSlotRecovery = {
      cohortId,
      cohortName,
      unsavedSlotIds: failed.map(failure => failure.slot.id),
      attempts,
      lastTriedAt: new Date().toISOString(),
    };
    updateTimeSlotRecovery(recovery);
    saveDraft(5, form.values, recovery);
    setActive(5);
    // The list of unsaved slots shows at the top of the step area.
    stepContentRef.current?.scrollTo({ top: 0 });

    hideFailureNotification();
    const totalSlots = form.values.timeSlots.length;
    const notificationId = `${TIME_SLOT_FAILURE_NOTIFICATION_PREFIX}-${attempts}`;
    failureNotificationIdRef.current = notificationId;
    // The alert in the wizard keeps the full list, so the notification closes
    // after the normal time.
    notifications.show({
      id: notificationId,
      ...timeSlotFailureNotification(failed.length, totalSlots, attempts),
      color: 'red',
    });
    return false;
  };

  // MED-1268: the cohort no longer exists, so its slots can never save. Clear
  // the draft, close the wizard and say why. The check when the wizard opens
  // keeps it open, blank, because the CM opened it to create a cohort.
  const handleCohortDeleted = (cohortName: string, { closeWizard = true } = {}) => {
    discardWizard();
    if (closeWizard) onClose();
    onCreated?.();
    notifications.show({ ...cohortDeletedNotification(cohortName), color: 'orange' });
  };

  // MED-1268 QA round 5: the wizard opened with a list of unsaved slots. The
  // cohort can be deleted in another tab or by another user, so check it. Only
  // a 404 clears the list; any other error keeps it, and Retry still works.
  const checkRecoveredCohort = async (recovery: TimeSlotRecovery) => {
    const result = await checkCohortStillExists(recovery.cohortId, id => cohortsApi.getById(id));
    if (result !== 'deleted') return;
    // A retry or a discard can finish while the check runs. Then there is
    // nothing left to clear.
    if (timeSlotRecoveryRef.current?.cohortId !== recovery.cohortId) return;
    handleCohortDeleted(recovery.cohortName, { closeWizard: false });
  };

  // MED-1268: sends only the slots that did not save, to the cohort that
  // already exists. It never creates the cohort again.
  const handleRetryTimeSlots = async () => {
    const recovery = timeSlotRecoveryRef.current;
    if (!recovery) return;
    setCreating(true);
    try {
      const unsavedSlots = (form.values.timeSlots || []).filter(slot =>
        recovery.unsavedSlotIds.includes(slot.id),
      );
      const allSaved = await saveTimeSlots(recovery.cohortId, recovery.cohortName, unsavedSlots);
      if (allSaved) finishCreation(recovery.cohortId, timeSlotsSavedNotification(recovery.cohortName));
    } finally {
      setCreating(false);
    }
  };

  // MED-1268: leaves the wizard for the cohort page. The draft and the list of
  // unsaved slots stay, so the CM can add them in Edit Cohort Details or come
  // back and retry.
  // It checks first that the cohort still exists, so a deleted cohort never
  // leads to a dead end.
  const handleGoToCreatedCohort = async () => {
    const recovery = timeSlotRecoveryRef.current;
    if (!recovery) return;
    setCheckingCohort(true);
    try {
      await cohortsApi.getById(recovery.cohortId);
    } catch (error) {
      if (isCohortNotFoundError(error)) {
        handleCohortDeleted(recovery.cohortName);
        return;
      }
      // Any other error: the cohort page shows its own error.
    } finally {
      setCheckingCohort(false);
    }
    hideFailureNotification();
    onClose();
    onCreated?.();
    navigate(`/cohorts/${recovery.cohortId}`);
  };

  const handleSubmit = async () => {
    // MED-1268: the cohort already exists. Retry the time slots only.
    if (timeSlotRecoveryRef.current) {
      await handleRetryTimeSlots();
      return;
    }

    try {
      // MED-1268: a resumed draft can skip the Partner & Program step too.
      if (!validateStep(0)) {
        setActive(0);
        notifications.show({
          title: 'Error',
          message: 'Please complete the required Partner & Program fields before creating the cohort',
          color: 'red',
        });
        return;
      }

      // AC6: a resumed draft restores its saved step directly, which can skip
      // the per-step gating in nextStep(). Re-validate the Cohort Details step
      // on submit so its required fields (name, short name, code) can never be
      // bypassed this way. Jump back to the step so the inline errors set by
      // validateStep are visible to the user.
      if (!validateStep(1)) {
        setActive(1);
        notifications.show({
          title: 'Error',
          message: 'Please complete the required Cohort Details fields before creating the cohort',
          color: 'red',
        });
        return;
      }

      // Validate the remaining required fields owned by other steps.
      if (!form.values.partnerId || form.values.selectedModalities.length === 0) {
        notifications.show({
          title: 'Error',
          message: 'Please complete all required fields before creating the cohort',
          color: 'red',
        });
        return;
      }

      setCreating(true);

      const { selectedModalities, groupCoachingConfig, oneOnOneConfig, workshopConfig, sessionDates } = form.values;
      const checkins = activeCheckins(form.values);
      // The cohort start and end come from the session dates (MED-1072 auto-fill included).
      const { groupCoachingDates, startDate, endDate } = deriveCohortDateRange(form.values);
      const weeks = programWeeks(startDate, endDate);

      // MED-1350: a resumed draft can skip the Dates step, so check the
      // target weeks against the program length again before the request.
      const checkinError = wizardCheckinsError(checkins, form.values);
      if (checkinError) {
        form.setFieldError('checkins', checkinError);
        setActive(4);
        notifications.show({ title: 'Error', message: checkinError, color: 'red' });
        return;
      }

      // Create new program if needed
      let programId = form.values.programId;
      if (programId === '__create_new__' && form.values.newProgramName.trim()) {
        const newProgram = await programsApi.create({
          partner_id: form.values.partnerId,
          name: form.values.newProgramName.trim(),
        });
        programId = newProgram.id;
      } else if (programId === '__create_new__') {
        // Clear the special value if no name provided
        programId = '';
      }

      // Build modalities array
      const modalities: CreateModalityInput[] = [];

      if (selectedModalities.includes('group_coaching')) {
        modalities.push({
          modality_type: 'group_coaching',
          session_count: groupCoachingConfig.sessionCount,
          session_frequency: groupCoachingConfig.frequency,
          session_duration_minutes: groupCoachingConfig.sessionDurationMinutes,
          // MED-848 — required for group_coaching, defaulted false for AI.
          coaching_sub_type: groupCoachingConfig.coachingSubType,
          // MED-948 (E1.10a) — persist the gate (AC5). AI features can only be
          // true when the gate is on; coerce defensively so a stale true never
          // reaches the API even though the backend also enforces this (AC4).
          live_session_delivery_enabled: groupCoachingConfig.liveSessionDeliveryEnabled,
          ai_features_enabled:
            groupCoachingConfig.liveSessionDeliveryEnabled &&
            groupCoachingConfig.aiFeaturesEnabled,
          // MED-1069 (E1.10c) — persist Commitment Tracking (AC9). INDEPENDENT of
          // live delivery (AC7) — no coercion, unlike ai_features_enabled.
          commitment_tracking_enabled: groupCoachingConfig.commitmentTrackingEnabled,
        });
      }

      if (selectedModalities.includes('one_on_one')) {
        modalities.push({
          modality_type: 'one_on_one',
          session_count: oneOnOneConfig.sessionCount,
          session_frequency: oneOnOneConfig.frequency,
          session_duration_minutes: oneOnOneConfig.sessionDurationMinutes,
          configuration: {
            session_configs: oneOnOneConfig.sessionConfigs.map((c) => ({
              session_number: c.sessionNumber,
              duration_minutes: c.durationMinutes,
              one_on_one_type: c.oneOnOneType,
              // MED-1350: program week of this 1:1, from its week date, never
              // past the program length.
              target_week: oneOnOneTargetWeek(sessionDates.one_on_one[c.sessionNumber - 1], startDate, weeks),
            })),
          },
        });
      }

      if (selectedModalities.includes('workshop')) {
        modalities.push({
          modality_type: 'workshop',
          configuration: { pattern: workshopConfig.pattern },
          // MED-848 — independent per-modality AI toggle (AC11).
          ai_features_enabled: workshopConfig.aiFeaturesEnabled,
          workshops: workshopConfig.workshops.map((w, i) => ({
            title: w.title,
            sequence_number: workshopConfig.pattern === 'sequential' ? i + 1 : undefined,
            participant_assignment: w.participant_assignment,
            facilitator_id: w.facilitator_id,
            attendance_tracking_enabled: w.attendance_tracking_enabled,
            session_duration_minutes: w.sessionDurationMinutes,
          })),
        });
      }

      // Build session dates from all modalities
      const allSessionDates: { session_number: number; scheduled_date: string }[] = [];

      // Helper to add dates for a modality
      const addModalityDates = (_modality: ModalityType, dates: (Date | null)[]) => {
        dates.forEach((date, index) => {
          const d = ensureDate(date);
          if (d) {
            allSessionDates.push({
              session_number: index + 1,
              scheduled_date: toISODateString(d)!,
            });
          }
        });
      };

      if (selectedModalities.includes('group_coaching')) {
        addModalityDates('group_coaching', groupCoachingDates);
      }

      // Build job title assignments (keyed by job_title_id)
      const jobTitleAssignments = Object.entries(form.values.teamAssignments)
        .map(([jobTitleId, userId]) => ({
          job_title_id: jobTitleId,
          user_id: userId || null,
        }))
        .filter(a => a.user_id !== null);

      // Filter out incomplete date constraints and convert to API format
      const validConstraints = form.values.dateConstraints
        .filter(c => ensureDate(c.start_date) && ensureDate(c.end_date))
        .map(c => ({
          start_date: toISODateString(ensureDate(c.start_date))!,
          end_date: toISODateString(ensureDate(c.end_date))!,
          label: c.label || undefined,
        }));

      // Build cohort key dates — CohortDates now stores Date | null directly
      const { cohortDates } = form.values;
      const coachImmersionDate = cohortDates.coachImmersion?.toISOString() ?? null;
      const participantOrientationDate = cohortDates.participantOrientation?.toISOString() ?? null;
      const coachHuddleDates = cohortDates.coachHuddles
        .filter((d): d is Date => d !== null)
        .map(d => d.toISOString());

      // Helper to get local date string (YYYY-MM-DD) avoiding UTC conversion issues
      const toLocalDateString = (date: Date | null): string | null => {
        if (!date) return null;

        if (date instanceof Date) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }

        // Handle string input
        const dateStr = String(date);
        // If already in YYYY-MM-DD format, return as-is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return dateStr;
        }
        // Otherwise parse and extract local date parts
        const parsed = new Date(dateStr);
        if (isNaN(parsed.getTime())) return null;
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const cohortWrapUpDate = toLocalDateString(cohortDates.cohortWrapUp);

      const cohortData: CreateCohortRequest = {
        partner_id: form.values.partnerId,
        program_id: programId || undefined,
        name: form.values.name,
        short_name: form.values.shortName || undefined,
        code: form.values.code || undefined,
        start_date: startDate,
        end_date: endDate,
        preferred_days: form.values.preferredDays,
        preferred_times: form.values.preferredTimes,
        modalities,
        session_dates: allSessionDates.length > 0 ? allSessionDates : undefined,
        date_constraints: validConstraints.length > 0 ? validConstraints : undefined,
        job_title_assignments: jobTitleAssignments.length > 0 ? jobTitleAssignments : undefined,
        primary_contact_ids: form.values.primaryContactIds.length > 0 ? form.values.primaryContactIds : undefined,
        // Key dates for cohort milestones
        coach_immersion_date: coachImmersionDate,
        participant_orientation_date: participantOrientationDate,
        coach_huddle_dates: coachHuddleDates.length > 0 ? coachHuddleDates : undefined,
        cohort_wrap_up_date: cohortWrapUpDate,
        default_meeting_platform: form.values.defaultMeetingPlatform || undefined,
        // MED-1350: a week is sent only for a check-in that is on.
        intro_checkin_enabled: checkins.introEnabled,
        intro_checkin_target_week: checkins.introEnabled ? checkins.introWeek : null,
        midpoint_checkin_enabled: checkins.midpointEnabled,
        midpoint_checkin_target_week: checkins.midpointEnabled ? checkins.midpointWeek : null,
      };

      const cohort = await cohortsApi.create(cohortData);

      // MED-1268: time slots are saved after the cohort exists. A slot failure
      // never rolls back the cohort, but it is never hidden either.
      const timeSlots = form.values.timeSlots || [];
      const allSaved = await saveTimeSlots(cohort.id, cohort.name || form.values.name, timeSlots);
      if (!allSaved) {
        onCreated?.();
        return;
      }

      finishCreation(cohort.id, { title: 'Success', message: 'Cohort created successfully' });
    } catch (error) {
      console.error('Cohort creation error:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create cohort',
        color: 'red',
      });
    } finally {
      setCreating(false);
    }
  };

  // Toggle modality selection
  const toggleModality = (modality: ModalityType) => {
    const current = form.values.selectedModalities;
    if (current.includes(modality)) {
      form.setFieldValue('selectedModalities', current.filter(m => m !== modality));
    } else {
      form.setFieldValue('selectedModalities', [...current, modality]);
    }
  };

  // Workshop management
  const addWorkshop = () => {
    form.setFieldValue('workshopConfig', {
      ...form.values.workshopConfig,
      workshops: [...form.values.workshopConfig.workshops, { ...DEFAULT_WORKSHOP_INPUT }],
    });
  };

  const removeWorkshop = (index: number) => {
    form.setFieldValue('workshopConfig', {
      ...form.values.workshopConfig,
      workshops: form.values.workshopConfig.workshops.filter((_, i) => i !== index),
    });
  };

  const updateWorkshop = (index: number, updates: Partial<FormWorkshopInput>) => {
    const workshops = [...form.values.workshopConfig.workshops];
    workshops[index] = { ...workshops[index], ...updates };
    form.setFieldValue('workshopConfig', {
      ...form.values.workshopConfig,
      workshops,
    });
  };

  // Add date constraint
  const addDateConstraint = () => {
    form.setFieldValue('dateConstraints', [
      ...form.values.dateConstraints,
      { start_date: null, end_date: null, label: '' },
    ]);
  };

  // Remove date constraint
  const removeDateConstraint = (index: number) => {
    form.setFieldValue(
      'dateConstraints',
      form.values.dateConstraints.filter((_, i) => i !== index),
    );
  };

  // Add coach huddle
  const addCoachHuddle = () => {
    form.setFieldValue('cohortDates', {
      ...form.values.cohortDates,
      coachHuddles: [...form.values.cohortDates.coachHuddles, null],
    });
  };

  // Remove coach huddle
  const removeCoachHuddle = (index: number) => {
    form.setFieldValue('cohortDates', {
      ...form.values.cohortDates,
      coachHuddles: form.values.cohortDates.coachHuddles.filter((_, i) => i !== index),
    });
  };

  // Add time slot
  const addTimeSlot = () => {
    const newSlot: FormTimeSlot = {
      id: Math.random().toString(36).substring(7),
      day_of_week: 'monday',
      time: '09:00',
      timezone: 'America/New_York',
    };
    const currentSlots = form.values.timeSlots || [];
    form.setFieldValue('timeSlots', [...currentSlots, newSlot]);
  };

  // Remove time slot
  const removeTimeSlot = (index: number) => {
    const currentSlots = form.values.timeSlots || [];
    form.setFieldValue(
      'timeSlots',
      currentSlots.filter((_, i) => i !== index),
    );
  };

  // Update time slot
  const updateTimeSlot = (index: number, updates: Partial<FormTimeSlot>) => {
    const currentSlots = form.values.timeSlots || [];
    const newSlots = [...currentSlots];
    newSlots[index] = { ...newSlots[index], ...updates };
    form.setFieldValue('timeSlots', newSlots);
  };

  // Get current constraints as DateConstraint array for shared utilities
  const getConstraints = useCallback((): DateConstraint[] => {
    return form.values.dateConstraints
      .filter(c => c.start_date && c.end_date)
      .map(c => ({
        start_date: c.start_date!,
        end_date: c.end_date!,
      }));
  }, [form.values.dateConstraints]);

  // Check if a date falls within any constraint
  const checkDateInConstraint = useCallback((date: Date | string | null): boolean => {
    return isDateInConstraint(date, getConstraints());
  }, [getConstraints]);

  // Get conflicts for visual indication (per modality)
  const sessionConflicts = useMemo(() => {
    const conflicts: Record<ModalityType, boolean[]> = {
      group_coaching: form.values.sessionDates.group_coaching.map(d => checkDateInConstraint(d)),
      one_on_one: form.values.sessionDates.one_on_one.map(d => checkDateInConstraint(d)),
      workshop: form.values.sessionDates.workshop.map(d => checkDateInConstraint(d)),
    };
    return conflicts;
  }, [form.values.sessionDates, checkDateInConstraint]);

  // Get selected partner/program for review
  const selectedPartner = partners.find(p => p.id === form.values.partnerId);
  const selectedProgram = programs.find(p => p.id === form.values.programId);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    return form.values.partnerId !== '' ||
      form.values.name !== '' ||
      form.values.selectedModalities.length > 0;
  }, [form.values.partnerId, form.values.name, form.values.selectedModalities]);

  // Handle modal close with unsaved changes confirmation
  // A plain function: it reads the latest recovery state and form values on
  // every render, and no child needs a stable reference.
  const handleClose = () => {
    // MED-1268: an old notification must not cover the confirm dialog.
    hideFailureNotification();
    if (timeSlotRecovery) {
      // MED-1268: the cohort exists. Only the list of unsaved slots is lost.
      // Keep List closes the wizard and keeps the draft, so Create Cohort
      // opens the list again. The X of this dialog stays in the wizard.
      modals.openConfirmModal({
        title: 'Time slots not saved',
        children: (
          <Text size="sm">
            The cohort {timeSlotRecovery.cohortName} is already created.{' '}
            {timeSlotFailureMessage(timeSlotRecovery.unsavedSlotIds.length, form.values.timeSlots.length)}{' '}
            {keepListConfirmText()} If you discard this list, add them by hand in Edit Cohort Details.
          </Text>
        ),
        labels: { confirm: 'Discard List', cancel: 'Keep List' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          discardWizard();
          onClose();
        },
        onCancel: () => {
          onClose();
        },
      });
    } else if (hasUnsavedChanges()) {
      modals.openConfirmModal({
        title: 'Unsaved Changes',
        children: (
          <Text size="sm">
            You have unsaved changes. Are you sure you want to close? Your progress will be lost.
          </Text>
        ),
        labels: { confirm: 'Discard Changes', cancel: 'Keep Editing' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          discardWizard();
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      // Check for saved draft when modal opens
      const draft = loadDraft(userId);
      if (draft?.values) {
        form.setValues(draft.values);
        resetCheckinAuto(draft.values);
        setActive(draft.step || 0);
        const recovery = draft.timeSlotRecovery ?? null;
        updateTimeSlotRecovery(recovery);
        if (recovery) void checkRecoveredCohort(recovery);
      } else if (timeSlotRecoveryRef.current) {
        // MED-1268: the list was cleared elsewhere, for example when the
        // cohort was deleted from its page. Do not show it again.
        discardWizard();
      }
    } else {
      // MED-1268: the wizard closed. Its notifications close with it.
      hideFailureNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, userId]);

  // MED-1268: the cohort exists, so its steps can no longer change.
  const stepsLocked = timeSlotRecovery !== null;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Create New Cohort"
      size={1100}
      centered
      closeOnClickOutside={false}
      classNames={{
        content: classes.modalContent,
        header: classes.modalHeader,
        body: classes.modalBody,
      }}
    >
      {/* Stepper */}
      <Box className={classes.stepperContainer}>
        <Stepper
          active={active}
          // MED-1268: with no click handler, Mantine draws the steps as not
          // clickable (default cursor, no tab stop).
          onStepClick={stepsLocked ? undefined : (step) => {
            // Allow going back freely, but validate current step before going forward
            if (step <= active) {
              setActive(step);
            } else if (validateStep()) {
              setActive(step);
            }
          }}
          className={classes.stepper}
          size="sm"
        >
          <Stepper.Step label="Partner & Program" disabled={stepsLocked} />
          <Stepper.Step label="Cohort Details" disabled={stepsLocked} />
          <Stepper.Step label="Modalities" disabled={stepsLocked} />
          <Stepper.Step label="Sessions" disabled={stepsLocked} />
          <Stepper.Step label="Dates" disabled={stepsLocked} />
          <Stepper.Step label="Review" disabled={stepsLocked} />
        </Stepper>
      </Box>

      {/* Step Content */}
      <Box className={classes.stepContent} ref={stepContentRef}>
        {timeSlotRecovery && (
          <TimeSlotSaveFailureAlert
            cohortName={timeSlotRecovery.cohortName}
            timeSlots={form.values.timeSlots || []}
            unsavedSlotIds={timeSlotRecovery.unsavedSlotIds}
            attempts={timeSlotRecovery.attempts}
            lastTriedAt={timeSlotRecovery.lastTriedAt}
          />
        )}

        {active === 0 && (
          <PartnerProgramStep
            form={form}
            partners={partners}
            programs={programs}
            partnerContacts={partnerContacts}
            loadingPartners={loadingPartners}
            loadingPrograms={loadingPrograms}
            loadingContacts={loadingContacts}
            onNavigateToPartners={() => {
              handleClose();
              navigate('/partners');
            }}
          />
        )}

        {active === 1 && (
          <CohortDetailsStep
            form={form}
            checkingCode={checkingCode}
            codeAvailable={codeAvailable}
            onCheckCode={checkCodeAvailability}
            checkingName={checkingName}
            nameAvailable={nameAvailable}
            onCheckName={checkNameAvailability}
            checkingShortName={checkingShortName}
            shortNameAvailable={shortNameAvailable}
            onCheckShortName={checkShortNameAvailability}
            internalUsers={internalUsers}
            jobTitleGroups={jobTitleGroups}
          />
        )}

        {active === 2 && (
          <ModalitiesStep
            form={form}
            onToggleModality={toggleModality}
          />
        )}

        {active === 3 && (
          <SessionConfigStep
            form={form}
            onAddWorkshop={addWorkshop}
            onRemoveWorkshop={removeWorkshop}
            onUpdateWorkshop={updateWorkshop}
            onCheckinsChange={handleCheckinsChange}
          />
        )}

        {active === 4 && (
          <DatesStep
            form={form}
            sessionConflicts={sessionConflicts}
            onAddConstraint={addDateConstraint}
            onRemoveConstraint={removeDateConstraint}
            onAddCoachHuddle={addCoachHuddle}
            onRemoveCoachHuddle={removeCoachHuddle}
            onAddTimeSlot={addTimeSlot}
            onRemoveTimeSlot={removeTimeSlot}
            onUpdateTimeSlot={updateTimeSlot}
            onCheckinsChange={handleCheckinsChange}
          />
        )}

        {active === 5 && (
          <ReviewStep
            form={form}
            selectedPartner={selectedPartner}
            selectedProgram={selectedProgram}
            partnerContacts={partnerContacts}
            internalUsers={internalUsers}
            jobTitleGroups={jobTitleGroups}
          />
        )}
      </Box>

      {/* Footer */}
      <Box className={classes.modalFooter}>
        <Button variant="subtle" onClick={handleClose}>
          Cancel
        </Button>

        <Group gap="sm">
          {timeSlotRecovery ? (
            // MED-1268: the cohort exists. Retry the unsaved slots, or go to the
            // cohort and add them in Edit Cohort Details.
            <>
              <Button
                variant="default"
                onClick={handleGoToCreatedCohort}
                loading={checkingCohort}
                disabled={creating}
              >
                Go to Cohort
              </Button>
              <Button color="red" onClick={handleRetryTimeSlots} loading={creating} disabled={checkingCohort}>
                Retry Time Slots
              </Button>
            </>
          ) : (
          <>
          {active > 0 && (
            <Button variant="default" onClick={prevStep} leftSection={<ArrowLeft size={16} />}>
              Back
            </Button>
          )}

          {active < 5 ? (
            <Button onClick={nextStep} rightSection={<ArrowRight size={16} />}>
              Continue
            </Button>
          ) : (
            <Button onClick={handleSubmit} loading={creating}>
              Create Cohort
            </Button>
          )}
          </>
          )}
        </Group>
      </Box>
    </Modal>
  );
}

// Legacy export for backward compatibility with route
export function CreateCohortPage() {
  const navigate = useNavigate();

  // Redirect to cohorts page with create param
  useEffect(() => {
    navigate('/cohorts?create=true', { replace: true });
  }, [navigate]);

  return null;
}

export default CreateCohortPage;
